import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ChunkyButton, Confetti, ComboPill, ShakeWidget, StarRow, FloatingXpBadge } from '../../components/GameUI';

// 30-second rapid-fire vocabulary quiz — answer as many multiple-choice
// questions as possible before the clock runs out. Every pick (right or
// wrong) reveals the answer briefly then auto-advances, since speed is the
// whole point. Port of the mobile app's lib/games/speed_quiz/speed_quiz_screen.dart.

const DURATION_SECONDS = 30;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeQuestion(pairs) {
  if (!pairs.length) return { current: null, options: [], picked: null, revealed: false };
  const current = pairs[Math.floor(Math.random() * pairs.length)];
  const distractors = shuffle(pairs.filter((p) => p.id !== current.id)).slice(0, 3);
  const options = shuffle([current, ...distractors]);
  return { current, options, picked: null, revealed: false };
}

function OptionTile({ text, dir, revealed, isAnswer, isPicked, onClick }) {
  let bg = '#FFFFFF';
  let border = '#E2E8F0';
  let fg = '#0F172A';
  if (revealed) {
    if (isAnswer) {
      bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00';
    } else if (isPicked) {
      bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000';
    } else {
      bg = '#FFFFFF'; border = '#F1F5F9'; fg = '#CBD5E1';
    }
  }
  return (
    <button
      type="button"
      dir={dir}
      disabled={revealed}
      onClick={onClick}
      style={{ background: bg, borderColor: border, color: fg }}
      className="flex min-h-[62px] items-center justify-center rounded-2xl border-[2.5px] px-3 py-2 text-center text-sm font-extrabold leading-tight transition-colors duration-150"
    >
      <span className="line-clamp-2">{text}</span>
    </button>
  );
}

export default function SpeedQuizGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    pairs = [],
    nativeLang = { code: '', label: '', dir: 'ltr' },
    targetLang = { code: '', label: '', dir: 'ltr' },
    langPair = 'guest',
  } = state || {};

  const [secondsLeft, setSecondsLeft] = useState(DURATION_SECONDS);
  const [question, setQuestion] = useState(() => makeQuestion(pairs));
  const [score, setScore] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [comboPopKey, setComboPopKey] = useState(0);
  const [done, setDone] = useState(false);

  const doneRef = useRef(false);
  const rewardedRef = useRef(false);

  useEffect(() => {
    if (!pairs.length) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    doneRef.current = done;
  }, [done]);

  // Countdown timer — ticks every second while the round is live, cancels on
  // unmount or when the round ends. Hitting zero ends the round in the same
  // update rather than a separate effect watching for secondsLeft <= 0.
  useEffect(() => {
    if (done) return undefined;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setDone(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [done]);

  const stars = score >= 15 ? 3 : score >= 8 ? 2 : 1;
  const xpEarned = score * 3 + (maxCombo >= 10 ? 10 : 0);

  useEffect(() => {
    if (!done || rewardedRef.current) return;
    rewardedRef.current = true;
    StreakService.recordActivity();
    XpService.addXp(xpEarned);
    if (stars === 3) SoundEffectService.playCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  function nextQuestion() {
    setQuestion(makeQuestion(pairs));
  }

  function pick(option) {
    if (question.revealed || doneRef.current) return;
    const current = question.current;
    const correct = option.id === current.id;
    setQuestion((q) => ({ ...q, picked: option.id, revealed: true }));

    if (correct) {
      setScore((s) => s + 1);
      setCombo((c) => {
        const nc = c + 1;
        setMaxCombo((mc) => Math.max(mc, nc));
        if (nc >= 3 && nc % 3 === 0) setComboPopKey((k) => k + 1);
        return nc;
      });
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `speedquiz:${current.id}` });
    } else {
      setWrong((w) => w + 1);
      setCombo(0);
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
    }

    setTimeout(() => {
      if (!doneRef.current) nextQuestion();
    }, 180);
  }

  function restart() {
    doneRef.current = false;
    rewardedRef.current = false;
    setSecondsLeft(DURATION_SECONDS);
    setScore(0);
    setWrong(0);
    setCombo(0);
    setMaxCombo(0);
    setComboPopKey(0);
    setDone(false);
    setQuestion(makeQuestion(pairs));
  }

  const urgent = secondsLeft <= 5;

  const resultEmoji = useMemo(() => {
    if (stars === 3) return '🏆';
    return score > 0 ? '⚡' : '💪';
  }, [stars, score]);

  if (!pairs.length) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6">
        <div className="mb-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50">
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.speedQuiz')}</h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-col items-center py-10 text-center">
            {stars === 3 && <Confetti />}
            <span className="text-6xl">{resultEmoji}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.speedQuizComplete', 'Time’s up!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.speedQuizScoreLine', '{{score}} correct • {{wrong}} wrong', { score, wrong })}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {t('games.wordMatchBestCombo', { combo: maxCombo })}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={restart} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button onClick={() => navigate('/games')} className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div
                className="rounded-full px-3.5 py-1.5 text-sm font-black text-white transition-transform duration-300"
                style={{ background: urgent ? '#FF4B4B' : '#4F46E5', transform: urgent ? 'scale(1.15)' : 'scale(1)' }}
              >
                ⏱ {secondsLeft}
              </div>
              <span className="text-sm font-black text-slate-900">
                {t('games.score')} {score}
              </span>
              <div className="min-w-[1px]">
                {combo >= 3 && <ComboPill key={comboPopKey} combo={combo} />}
              </div>
            </div>

            <div className="mt-7 text-center">
              <p dir={targetLang.dir} className="text-3xl font-black text-slate-900">
                {question.current?.target}
              </p>
              <p className="mt-1.5 text-xs text-slate-400">
                {t('games.translateTo', 'Translate to {{lang}}', { lang: nativeLang.label })}
              </p>
            </div>

            <div dir={nativeLang.dir} className="mt-6 grid grid-cols-2 gap-2.5">
              {question.options.map((option) => {
                const isAnswer = option.id === question.current?.id;
                const isPicked = option.id === question.picked;
                return (
                  <ShakeWidget key={option.id} trigger={question.revealed && isPicked && !isAnswer}>
                    <OptionTile
                      text={option.native}
                      dir={nativeLang.dir}
                      revealed={question.revealed}
                      isAnswer={isAnswer}
                      isPicked={isPicked}
                      onClick={() => pick(option)}
                    />
                  </ShakeWidget>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
